/**
 * Phase 3 — BaselineVsBestCard
 *
 * Procurement-grade hero card. Two columns: baseline metrics (left)
 * and recommended metrics (right) with green/red deltas. Below: the
 * variable_deltas list as delta pills, the summary line, the
 * objective_met badge, and the caveats list.
 *
 * Phase 3.6 additions (additive only; no field renames or shape changes):
 *   • Pk row carries a `± pk_se` half-width when ``pk_se > 0``.
 *   • A statistical-significance pill renders under the recommended
 *     Pk based on ``statistically_distinct_from_baseline``.
 *   • A one-liner pulled from ``recommendation_explanation`` lands
 *     just under the metric grid.
 *
 * Consumes the /diff response shape exactly.
 */
import {
    AlertTriangle, ArrowDownRight, ArrowUpRight, BadgeCheck, ShieldCheck, Star, ShieldQuestion,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import CredibilityBadge from "@/components/CredibilityBadge";

function fmt(n, digits = 2) {
    if (typeof n !== "number" || Number.isNaN(n)) return "—";
    return n.toFixed(digits);
}

function fmtPct(n) {
    if (typeof n !== "number" || Number.isNaN(n)) return null;
    return `${n >= 0 ? "+" : ""}${n.toFixed(1)} %`;
}

function fmtPkWithSe(pk, se, digits = 3) {
    if (typeof pk !== "number" || Number.isNaN(pk)) return "—";
    if (typeof se === "number" && se > 0) {
        return (
            <>
                {pk.toFixed(digits)}
                <span className="text-ux-text-3"> ± {se.toFixed(digits)}</span>
            </>
        );
    }
    return pk.toFixed(digits);
}

function MetricRow({
    label, baseline, recommended,
    betterDirection = "higher", digits = 2, unit = "",
    baselineSe = null, recommendedSe = null,
    extra = null,
    testId = null,
}) {
    const hasBoth =
        typeof baseline === "number" && typeof recommended === "number";
    const delta = hasBoth ? recommended - baseline : null;
    const epsilon = Math.pow(10, -(digits + 1));
    const isImprovement =
        delta !== null && Math.abs(delta) > epsilon
            ? betterDirection === "higher"
                ? delta > 0
                : delta < 0
            : null;
    const Arrow = delta !== null && delta >= 0 ? ArrowUpRight : ArrowDownRight;
    return (
        <div
            className="grid grid-cols-3 items-baseline gap-2 py-1.5 border-b border-ux-border-1 last:border-b-0"
            data-testid={testId}
        >
            <div className="text-[11px] text-ux-text-3 uppercase tracking-wider">
                {label}
            </div>
            <div className="text-[14px] tabular-nums text-ux-text-2 font-mono">
                {fmtPkWithSe(baseline, baselineSe, digits)}
                {unit}
            </div>
            <div className="flex flex-col">
                <div className="flex items-baseline gap-2">
                    <span
                        className={[
                            "text-[14px] tabular-nums font-mono font-medium",
                            isImprovement === true
                                ? "text-ux-success"
                                : isImprovement === false
                                  ? "text-ux-danger"
                                  : "text-ux-text-1",
                        ].join(" ")}
                    >
                        {fmtPkWithSe(recommended, recommendedSe, digits)}
                        {unit}
                    </span>
                    {delta !== null && Math.abs(delta) > epsilon && (
                        <span
                            className={[
                                "inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded",
                                isImprovement === true
                                    ? "bg-ux-success-soft text-ux-success"
                                    : "bg-ux-danger-soft text-ux-danger",
                            ].join(" ")}
                        >
                            <Arrow className="w-3 h-3 mr-0.5" />
                            {fmt(Math.abs(delta), digits)}
                        </span>
                    )}
                </div>
                {extra}
            </div>
        </div>
    );
}

function StatSignificancePill({ distinct }) {
    if (distinct === true) {
        return (
            <span
                className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-wider text-ux-success bg-ux-success-soft px-1.5 py-0.5 rounded self-start"
                title="The 95% confidence intervals around the baseline and recommended Pk do not overlap."
                data-testid="baseline-vs-best-stat-distinct"
            >
                <BadgeCheck className="w-3 h-3" /> Statistically significant uplift
            </span>
        );
    }
    if (distinct === false) {
        return (
            <span
                className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-wider text-ux-warning bg-ux-warning-soft px-1.5 py-0.5 rounded self-start"
                title="Confidence intervals on baseline and recommended Pk overlap, so the difference is not statistically distinguishable at the current seed budget."
                data-testid="baseline-vs-best-stat-noise"
            >
                <ShieldQuestion className="w-3 h-3" /> Within statistical noise
            </span>
        );
    }
    return null;
}

const BASIS_PILL = {
    primary_rank_only: {
        label: "Primary rank only",
        tooltip: "Confirmation pass was not run; recommendation rests on the primary rank.",
        className: "bg-ux-bg-3 text-ux-text-3",
    },
    primary_rank_confirmed: {
        label: "Confirmed at higher fidelity",
        tooltip: "Confirmation pass agreed with the primary rank — the top candidate stayed elected.",
        className: "bg-ux-accent-soft text-ux-accent",
    },
    confirmation_re_elected: {
        label: "Re-elected by confirmation pass",
        tooltip: "Confirmation pass swapped the elected candidate after re-running the top-N at higher seeds.",
        className: "bg-ux-accent-soft text-ux-accent",
    },
    cancelled_during_confirmation: {
        label: "Cancelled mid-confirmation",
        tooltip: "Confirmation pass was cancelled before completing; primary rank stands as the recommendation.",
        className: "bg-ux-warning-soft text-ux-warning",
    },
};

function RecommendationBasisPill({ basis }) {
    const cfg = BASIS_PILL[basis];
    if (!cfg) return null;
    return (
        <span
            className={[
                "inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded self-start",
                cfg.className,
            ].join(" ")}
            title={cfg.tooltip}
            data-testid={`baseline-vs-best-basis-${basis}`}
        >
            {cfg.label}
        </span>
    );
}

function ConfirmationBadge({ rec }) {
    if (!rec || !rec.confirmed) return null;
    const pk = rec.confirmation_pk;
    const se = rec.confirmation_pk_se;
    const diverged = !!rec.confirmation_disagreement;
    if (typeof pk !== "number") return null;
    return (
        <div
            className="flex items-center gap-2 mt-1 text-[11px] text-ux-text-2"
            data-testid="baseline-vs-best-confirmation"
        >
            <span className="text-ux-text-3 uppercase tracking-wider text-[9px]">
                Confirmed
            </span>
            <span className="font-mono tabular-nums text-ux-text-1">
                {pk.toFixed(3)}
                {typeof se === "number" && se > 0 && (
                    <span className="text-ux-text-3"> ± {se.toFixed(3)}</span>
                )}
            </span>
            {diverged && (
                <span
                    className="text-[9px] uppercase tracking-wider bg-ux-warning-soft text-ux-warning px-1.5 py-0.5 rounded"
                    title="Confirmation Pk fell outside the predicted CI."
                    data-testid="baseline-vs-best-confirmation-diverged"
                >
                    Confirmation diverged
                </span>
            )}
        </div>
    );
}

export default function BaselineVsBestCard({ diff, onPromote, compact = false }) {
    if (!diff) return null;
    const bl = diff.baseline || {};
    const rec = diff.recommended;

    if (rec == null) {
        return (
            <div
                className="rounded-md border border-ux-warning/40 bg-ux-warning-soft/30 p-4 space-y-2"
                data-testid="baseline-vs-best-no-recommendation"
            >
                <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-ux-warning" />
                    <span className="text-[13px] text-ux-warning font-medium">
                        No recommended configuration
                    </span>
                </div>
                <p className="text-[12px] text-ux-text-2">{diff.summary_line}</p>
            </div>
        );
    }

    const objMet = !!diff.objective_met;
    const deltas = rec.variable_deltas || [];
    const cavs = rec.caveats || [];
    const explanation = diff.recommendation_explanation;
    const distinct = typeof rec.statistically_distinct_from_baseline === "boolean"
        ? rec.statistically_distinct_from_baseline
        : null;

    return (
        <div
            className={[
                "rounded-md border bg-ux-bg-1",
                objMet ? "border-ux-success/40" : "border-ux-border-1",
            ].join(" ")}
            data-testid="baseline-vs-best-card"
        >
            <div className="flex items-center justify-between gap-3 p-4 border-b border-ux-border-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <Star className="w-4 h-4 text-ux-accent" />
                    <span className="text-[13px] text-ux-text-1 font-medium">
                        Recommended configuration
                    </span>
                    <CredibilityBadge
                        tier={rec.confidence_tier}
                        testId="baseline-vs-best-recommended-tier"
                    />
                    {objMet ? (
                        <span
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-ux-success bg-ux-success-soft px-2 py-0.5 rounded"
                            data-testid="baseline-vs-best-objective-met"
                        >
                            <ShieldCheck className="w-3 h-3" /> Objective met
                        </span>
                    ) : (
                        <span
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-ux-warning bg-ux-warning-soft px-2 py-0.5 rounded"
                            data-testid="baseline-vs-best-objective-unmet"
                        >
                            <AlertTriangle className="w-3 h-3" /> Below target
                        </span>
                    )}
                    <RecommendationBasisPill basis={diff.recommendation_basis} />
                </div>
                {onPromote && rec.candidate_id && (
                    <Button
                        size="sm"
                        onClick={() => onPromote(rec.candidate_id)}
                        data-testid="baseline-vs-best-promote-button"
                    >
                        Promote configuration
                    </Button>
                )}
            </div>

            <div className="p-4 space-y-2">
                <div className="grid grid-cols-3 gap-2 text-[10px] uppercase tracking-wider text-ux-text-3 pb-1 border-b border-ux-border-1">
                    <span>Metric</span>
                    <span>Baseline</span>
                    <span>Recommended</span>
                </div>
                <MetricRow
                    label="Pk"
                    baseline={bl.pk}
                    recommended={rec.pk}
                    digits={3}
                    baselineSe={bl.pk_se}
                    recommendedSe={rec.pk_se}
                    extra={
                        <>
                            <StatSignificancePill distinct={distinct} />
                            <ConfirmationBadge rec={rec} />
                        </>
                    }
                    testId="baseline-vs-best-pk-row"
                />
                <MetricRow
                    label="Mean miss distance"
                    baseline={bl.mean_miss_distance_m}
                    recommended={rec.mean_miss_distance_m}
                    betterDirection="lower"
                    digits={2}
                    unit=" m"
                />
                <MetricRow
                    label="P95 miss distance"
                    baseline={bl.p95_miss_distance_m}
                    recommended={rec.p95_miss_distance_m}
                    betterDirection="lower"
                    digits={2}
                    unit=" m"
                />
                <MetricRow
                    label="Mean intercept time"
                    baseline={bl.mean_intercept_time_s}
                    recommended={rec.mean_intercept_time_s}
                    betterDirection="lower"
                    digits={1}
                    unit=" s"
                />
                <MetricRow
                    label="Design cost"
                    baseline={0}
                    recommended={rec.design_cost}
                    betterDirection="lower"
                    digits={2}
                />
                {explanation && (
                    <p
                        className="text-[12px] text-ux-text-2 italic pt-2"
                        data-testid="baseline-vs-best-explanation"
                    >
                        {explanation}
                    </p>
                )}
            </div>

            {!compact && deltas.length > 0 && (
                <div className="p-4 border-t border-ux-border-1 space-y-2">
                    <h4 className="text-[11px] uppercase tracking-wider text-ux-text-3">
                        Configuration changes
                    </h4>
                    <ul className="space-y-1.5" data-testid="baseline-vs-best-deltas">
                        {deltas.map((d, idx) => (
                            <li
                                key={idx}
                                className="flex items-center gap-2 text-[12px]"
                                data-testid={`baseline-vs-best-delta-${idx}`}
                            >
                                <span className="text-ux-text-2 flex-1 truncate">
                                    {d.label || d.path}
                                </span>
                                <span className="font-mono tabular-nums text-ux-text-3">
                                    {fmt(d.baseline, 2)}
                                </span>
                                <span className="text-ux-text-3">→</span>
                                <span className="font-mono tabular-nums text-ux-text-1">
                                    {fmt(d.candidate, 2)}
                                </span>
                                {d.delta_pct !== null && d.delta_pct !== undefined && (
                                    <span
                                        className={[
                                            "text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ml-auto",
                                            (d.delta || 0) >= 0
                                                ? "bg-ux-info-soft text-ux-info"
                                                : "bg-ux-bg-3 text-ux-text-3",
                                        ].join(" ")}
                                    >
                                        {fmtPct(d.delta_pct)}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="p-4 border-t border-ux-border-1 space-y-2">
                <p
                    className="text-[12px] text-ux-text-1"
                    data-testid="baseline-vs-best-summary"
                >
                    {diff.summary_line}
                </p>
                {cavs.length > 0 && (
                    <ul className="space-y-1" data-testid="baseline-vs-best-caveats">
                        {cavs.map((c, idx) => (
                            <li
                                key={idx}
                                className="text-[11px] text-ux-text-3 italic flex items-start gap-2"
                            >
                                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0 text-ux-warning" />
                                <span>{c}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
